import { Directive, ElementRef, EventEmitter, NgZone, OnDestroy, Output } from '@angular/core';
import { MapsAPILoader } from '@agm/core';
import { ZoneLocation } from './alarm-zone.model';

declare var google: any;

/**
 * Podpina podpowiedzi adresowe Google Places do pola tekstowego i zamyka w sobie
 * wszystko, co brudne: cykl życia widgetu, sprzątanie listy podpowiedzi i wykrycie
 * momentu, w którym użytkownik wybiera pozycję z listy.
 *
 * Dwie pułapki, które wcześniej były rozsypane po komponentach:
 *
 * 1. Każdy `new Autocomplete(...)` dokleja do <body> własny `.pac-container`.
 *    Podpinanie się wielokrotnie do tego samego pola zostawiało osierocone listy,
 *    które potrafiły wisieć nad ekranem, bo nie miały już kto ich zamknąć.
 * 2. Kliknięcie podpowiedzi najpierw powoduje `blur` pola, a `place_changed`
 *    przychodzi dopiero po sieciowym pobraniu szczegółów miejsca. Kto reaguje na
 *    `blur`, musi wiedzieć, że wybór jest w toku — stąd `selectionPending`.
 */
@Directive({
  selector: '[appPlacesAutocomplete]'
})
export class PlacesAutocompleteDirective implements OnDestroy {

  @Output() placeSelected = new EventEmitter<ZoneLocation>();

  private pacContainer: HTMLElement = null;
  private bound = false;
  private selectionPending = false;

  constructor(
    private elementRef: ElementRef,
    private mapsAPILoader: MapsAPILoader,
    private ngZone: NgZone,
  ) {
    this.mapsAPILoader.load().then(() => this.bind());
  }

  ngOnDestroy() {
    if (this.pacContainer && this.pacContainer.parentNode) {
      this.pacContainer.parentNode.removeChild(this.pacContainer);
    }
    this.pacContainer = null;
    if (this.bound) {
      document.removeEventListener('mousedown', this.onDocumentMouseDown, true);
      this.bound = false;
    }
  }

  /**
   * Czy trwa wybór pozycji z listy. Odczyt kasuje flagę, bo interesuje nas
   * wyłącznie ten jeden `blur`, który wybór poprzedza.
   */
  consumeSelectionPending(): boolean {
    const pending = this.selectionPending;
    this.selectionPending = false;
    return pending;
  }

  /** Zamyka listę podpowiedzi — pole bywa chowane przez display:none, a wtedy Google nie dostaje blur. */
  hideSuggestions() {
    if (this.pacContainer) {
      this.pacContainer.style.display = 'none';
    }
  }

  private bind() {
    if (this.bound) {
      return;
    }
    const input: HTMLInputElement = this.elementRef.nativeElement;

    const before = this.pacContainers();
    const autocomplete = new google.maps.places.Autocomplete(input, { types: ['address'] });
    this.pacContainer = this.pacContainers().filter(el => before.indexOf(el) === -1)[0] || null;

    document.addEventListener('mousedown', this.onDocumentMouseDown, true);
    this.bound = true;

    autocomplete.addListener('place_changed', () => {
      this.ngZone.run(() => {
        const place = autocomplete.getPlace();
        if (!place || !place.geometry) {
          return;
        }
        this.selectionPending = false;
        this.placeSelected.emit({
          address: place.formatted_address,
          latitude: place.geometry.location.lat(),
          longitude: place.geometry.location.lng()
        });
      });
    });
  }

  private pacContainers(): HTMLElement[] {
    return Array.prototype.slice.call(document.querySelectorAll('.pac-container'));
  }

  private onDocumentMouseDown = (event: MouseEvent) => {
    let node = <HTMLElement>event.target;
    while (node) {
      if (node.classList && node.classList.contains('pac-container')) {
        this.selectionPending = true;
        return;
      }
      node = node.parentElement;
    }
  }
}
